import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, Loader2, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";

export default function Login() {
  const { lang } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const redirect = sp.get("redirect") || "/profile";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success(lang === "tr" ? "Hoş geldin!" : "Welcome back!");
      navigate(redirect);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="login-page">
      <div className="text-center mb-10">
        <span className="eyebrow text-red-500">{lang === "tr" ? "GİRİŞ" : "SIGN IN"}</span>
        <h1 className="headline text-4xl sm:text-5xl mt-3 text-white">
          {lang === "tr" ? "Tekrar hoş geldin" : "Welcome back"}
        </h1>
        <p className="mt-3 text-neutral-400">
          {lang === "tr" ? "Hesabınla giriş yap." : "Sign in with your account."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-neutral-950 border border-neutral-800 rounded-sm p-8" data-testid="login-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-2">
            {lang === "tr" ? "E-posta" : "Email"}
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-600"
              data-testid="login-email"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-400 block mb-2">
            {lang === "tr" ? "Şifre" : "Password"}
          </label>
          <div className="relative">
            <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm pl-10 pr-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-600"
              data-testid="login-password"
            />
          </div>
        </div>
        {err && (
          <div className="text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-sm px-3 py-2" data-testid="login-error">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors disabled:opacity-60"
          data-testid="login-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
          {lang === "tr" ? "Giriş Yap" : "Sign In"}
        </button>
        <p className="text-sm text-center text-neutral-400">
          {lang === "tr" ? "Hesabın yok mu?" : "No account?"}{" "}
          <Link to={`/register?redirect=${encodeURIComponent(redirect)}`} className="text-red-500 hover:text-red-400" data-testid="link-register">
            {lang === "tr" ? "Hemen kayıt ol" : "Create one"}
          </Link>
        </p>
      </form>
    </div>
  );
}
